import { useState } from "react";
import hotToast from "react-hot-toast";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Button } from "../../../Components/ui/button";
import { FourDots } from "../../../Components/FourDots";
import StepOrganization from "./StepOrganization";
import StepGeneralInfo from "./StepGeneralInfo";
import StepSpark from "../../../Components/StepSpark/StepSpark";
import Avatar from "../../../Components/Profile/Avatar/Avatar";
import { useNavigate } from "react-router-dom";
import measureLogo from "/images/7bef2f4973b64f555e9540afb88ba765fa41f030.png";
import {
  updateFieldNotListedForSubmission,
  notListedInputFieldHasError,
} from "../../../utils/onboarding";
import {
  useUpdateUserMutation,
  useUpdateProfilePictureMutation,
  useGetProfileQuery,
  useAddOrganizationMutation,
  useGetSparksQuery,
} from "../../../../redux/reducers/apiSlice";
import FullPageNexusLogoAnimation from "../../../Reusable/FullPageNexusLogoAnimation/FullPageNexusLogoAnimation";
import { signupOnboardingSchema } from "../../Organizations/AddNewOrganizationForm/schema";
import { customConsole } from "../../../utils/helper";
import {
  buildUserUpdatePayload,
  getApiErrorMessage,
  sanitizeOrganizationPayload,
} from "../../../utils/apiPayloads";
import * as Sentry from "@sentry/react";
import useSignout from "../../../hooks/useSignout";

const SignUpOnboarding = (props) => {
  const [step, setStep] = useState("ORGANIZATION");
  const navigate = useNavigate();
  const [updateUser, { isLoading }] = useUpdateUserMutation();
  const [addOrganization, { isLoading: isAddingOrganization }] = useAddOrganizationMutation();
  const dispatch = useDispatch();
  const {
    data: currentProfileData,
    isLoading: currentProfileDataIsLoading,
  } = useGetProfileQuery();
  const { data: sparksData } = useGetSparksQuery();
  const sparksAreAvailable = (sparksData?.length ?? 0) > 0;

  const [updateProfilePicture] = useUpdateProfilePictureMutation();
  const { signOut } = useSignout();

  const handleFinish = async (data) => {
    const notListedInputEmpty =
      notListedInputFieldHasError(data, "gender", hotToast) ||
      notListedInputFieldHasError(data, "ethnicity", hotToast) ||
      notListedInputFieldHasError(data, "pronouns", hotToast);

    if (notListedInputEmpty) {
      setStep("PROFILE");
      return;
    }

    try {
      // Ensure user has exactly one organization after onboarding (free tier = 1 org).
      // If they filled the org step, create that org; otherwise create a default "My Organization".
      if (data.organization?.name && data.organization?.type && data.organization?.location) {
        try {
          const locationParts = data.organization.location.split(",").map(s => s.trim());
          const city = locationParts[0] || "";
          const state = locationParts[1] || "";

          const orgPayload = sanitizeOrganizationPayload({
            name: data.organization.name,
            type: data.organization.type,
            mission: data.organization.mission || "",
            phoneNumber: "0000000000",
            location: {
              city,
              state,
              zip: "",
              address1: "",
              address2: "",
              country: "United States",
            },
          });
          await addOrganization(orgPayload).unwrap();
          hotToast.success("Organization created successfully");
        } catch (orgError) {
          Sentry.captureException(orgError);
          console.error("OrganizationCreationError", orgError);
        }
      } else {
        // User skipped the org step: create a default org so they have 1 org (free tier limit).
        try {
          const city = data.location?.city || "";
          const state = data.location?.state || "";
          const orgPayload = sanitizeOrganizationPayload({
            name: "My Organization",
            type: "non-profit",
            mission: "",
            phoneNumber: "0000000000",
            location: {
              city,
              state,
              zip: "",
              address1: "",
              address2: "",
              country: "United States",
            },
          });
          await addOrganization(orgPayload).unwrap();
        } catch (orgError) {
          Sentry.captureException(orgError);
          console.error("OrganizationCreationError", orgError);
        }
      }

      // Update user profile
      const payload = buildUserUpdatePayload(
        {
          ...data,
          gender: updateFieldNotListedForSubmission(data.gender, data.genderText),
          pronouns: updateFieldNotListedForSubmission(
            data.pronouns,
            data.pronounsText
          ),
          ethnicity: updateFieldNotListedForSubmission(
            data.ethnicity,
            data.ethnicityText
          ),
        },
        {
          email: currentProfileData?.email,
          includeSparks: true,
          // Ensure AppRoutes can switch out of onboarding immediately after a successful update.
          onboarded: true,
        }
      );

      await updateUser(payload).unwrap();
      // Navigate to root - AppRoutes will show dashboard when onboarded becomes true
      navigate("/");
    } catch (error) {
      Sentry.captureException(error);
      console.error("SignupOnboardingError", error);
      hotToast.error(getApiErrorMessage(error));
    }
  };

  if (isLoading || currentProfileDataIsLoading || isAddingOrganization) {
    return <FullPageNexusLogoAnimation />;
  }

  // Progress indicator dots
  const steps = ["ORGANIZATION", "PROFILE", "SPARKS"];
  const currentStepIndex = steps.indexOf(step);

  return (
    <>
      <style>{`
        .chakra-simple-grid {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        .not-listed-field {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        .not-listed-field .chakra-flex {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        .not-listed-field .chakra-input {
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
      `}</style>
      <div className="min-h-screen flex">
        {/* Left side - Form */}
        <div className="flex-1 flex items-start justify-center p-8 overflow-y-auto">
          <div className="w-full max-w-md py-8">
          {/* Progress indicator */}
          <div className="flex justify-center gap-2 mb-6">
            {steps.map((s, idx) => (
              <div
                key={s}
                className={`h-2 w-16 rounded-full ${
                  idx <= currentStepIndex ? "bg-orange-500" : "bg-gray-200"
                }`}
              />
            ))}
          </div>

          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <Link to="/" className="mb-8">
              <img
                src={measureLogo}
                alt="Measure Ignite"
                className="h-25 w-auto"
              />
            </Link>
          </div>

          {/* Avatar for Profile step */}
          {step === "PROFILE" && (
            <div className="flex justify-center mb-6">
              <Avatar
                defaultImage={currentProfileData?.image?.url}
                updateImage={updateProfilePicture}
                id={currentProfileData?.id}
              />
            </div>
          )}

          <Formik
            validationSchema={signupOnboardingSchema}
            validateOnMount={false}
            validateOnChange={false}
            validateOnBlur={false}
            initialValues={{
              organization: {
                name: "",
                type: "",
                location: "",
                mission: "",
              },
              name: {
                firstName: "",
                lastName: "",
              },
              title: "",
              location: {
                country: "United States",
                state: "",
                city: "",
                zip: "",
              },
              gender: "",
              genderText: "",
              pronouns: "",
              pronounsText: "",
              ethnicity: "",
              ethnicityText: "",
              sparks: [],
            }}
            onSubmit={async (data, handlers) => {
              handlers.setSubmitting(true);

              try {
                // Only validate and submit if we're on the SPARKS step
                if (step === "SPARKS") {
                  // Validate sparks ONLY if backend provides sparks.
                  // Local backends sometimes don't seed sparks yet, which would otherwise block onboarding.
                  if (sparksAreAvailable) {
                    const sparksValid = data.sparks && data.sparks.length === 3;
                    if (!sparksValid) {
                      handlers.setFieldError("sparks", "You should choose 3 sparks");
                      return;
                    }
                  }

                  // Check if required profile fields are filled
                  const profileFieldsValid =
                    data.name?.firstName &&
                    data.name?.lastName &&
                    data.title &&
                    data.location?.state &&
                    data.location?.city &&
                    data.gender &&
                    data.pronouns &&
                    data.ethnicity;

                  if (!profileFieldsValid) {
                    hotToast.error("Please complete all profile fields before finishing");
                    setStep("PROFILE");
                    return;
                  }

                  // All validations passed, proceed with finish (await so errors surface)
                  await handleFinish(data);
                }
              } finally {
                handlers.setSubmitting(false);
              }
            }}
          >
            {(formik) => {
              function disableButton(step: string): boolean {
                // ORGANIZATION step is optional: user can skip (we create default org on finish).
                if (step === "ORGANIZATION") return false;
                if (step === "PROFILE") {
                  if (
                    formik.values.name.firstName &&
                    formik.values.name.lastName &&
                    formik.values.title &&
                    formik.values.location &&
                    formik.values.location.state &&
                    formik.values.location.city &&
                    formik.values.gender &&
                    formik.values.pronouns &&
                    formik.values.ethnicity
                  ) {
                    return false;
                  }
                }
                if (step === "SPARKS") {
                  // If sparks are not available from backend, don't block onboarding.
                  if (!sparksAreAvailable) return false;
                  if (formik.values.sparks && formik.values.sparks.length === 3) return false;
                }
                return true;
              }

              return (
                <Form onSubmit={formik.handleSubmit} className="space-y-4">
                  <div>
                    {step === "ORGANIZATION" && (
                      <StepOrganization formik={formik} />
                    )}
                    {step === "PROFILE" && (
                      <StepGeneralInfo formik={formik} />
                    )}
                    {step === "SPARKS" && <StepSpark formik={formik} />}
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex justify-between items-center pt-4 mt-6 border-t border-gray-200">
                    {step === "ORGANIZATION" && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={signOut}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          className="bg-orange-500 hover:bg-orange-600"
                          disabled={disableButton("ORGANIZATION")}
                          onClick={() => setStep("PROFILE")}
                        >
                          Next
                        </Button>
                      </>
                    )}

                    {step === "PROFILE" && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setStep("ORGANIZATION")}
                        >
                          Back
                        </Button>
                        <Button
                          type="button"
                          className="bg-orange-500 hover:bg-orange-600"
                          disabled={disableButton("PROFILE")}
                          onClick={() => setStep("SPARKS")}
                        >
                          Next
                        </Button>
                      </>
                    )}

                    {step === "SPARKS" && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setStep("PROFILE")}
                        >
                          Back
                        </Button>
                        <Button
                          type="submit"
                          className="bg-orange-500 hover:bg-orange-600"
                          disabled={disableButton("SPARKS")}
                        >
                          Finish
                        </Button>
                      </>
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-orange-100 via-pink-100 to-orange-50 p-12">
        <div className="h-full flex flex-col justify-start items-center pt-12">
          <div className="max-w-lg text-center mb-8">
            {step === "ORGANIZATION" && (
              <>
                <h2 className="text-3xl font-semibold mb-4">
                  Tell us about your organization
                </h2>
                <p className="text-gray-700">
                  This helps us create your organization profile
                </p>
              </>
            )}

            {step === "PROFILE" && (
              <>
                <h2 className="text-3xl font-semibold mb-4">
                  Complete your profile
                </h2>
                <p className="text-gray-700">
                  Help your community get to know you
                </p>
              </>
            )}

            {step === "SPARKS" && (
              <>
                <h2 className="text-3xl font-semibold mb-4">
                  What's your spark?
                </h2>
                <p className="text-gray-700">
                  At Measure, we identify your sparks as the causes that motivate
                  you to ignite change.
                </p>
              </>
            )}
          </div>
          <FourDots size="2xl" opacity={0.7} />
        </div>
      </div>
    </div>
    </>
  );
};

export default SignUpOnboarding;
